/* eslint-disable complexity */
import React, {
  useState,
  useCallback,
  useMemo,
  useContext,
  useEffect
} from 'react';
import _get from 'lodash/get';
import {
  Container,
  Typography,
  makeStyles,
  useMediaQuery
} from '@material-ui/core';

import { history } from '../../../Routes/history';
import { ROUTE_NAMES } from '../../../Routes/Routes';

import Stepper from '../../../Components/Stepper/Stepper';
import FullSizeProgress from '../../../Components/Progress/FullSize/FullSizeProgress';
import BaseDialog from '../../../Components/Dialogs/BaseDialog';
import BaseButton from '../../../Components/Buttons/BaseButton';
import { defaultAlertError } from '../../../Components/Alerts/AlertMessages';
import { ALERT_TYPE } from '../../../Components/Alerts/alert_enums';

import { extractErrorMessage } from '../../../Utils/Errors/Errors';

import {
  UserContext,
  UserDispatchContext
} from '../../../Contexts/UserContext';
import { AlertsDispatchContext } from '../../../Contexts/AlertsContext';

import SecondStep from '../SecondStep/Second';
import PaymentOptions from '../FirstStep/PaymentOptions';
import {
  INDIVIDUAL_STATE,
  INDIVIDUAL_LOAD_INITIAL_STATE,
  MULTIPLE_LOAD_INITIAL_STATE,
  SELECT_OPTIONS,
  TABS_INITIAL_STATE,
  TRANSACTION_STATUS,
  TYPE_OF_PAYMENTS,
  TRANSACTION_TYPES,
  PAYMENT_VALUE_OPTION,
  PAYMENT_STEPS,
  PAYMENT_METHOD,
  FORM_ID
} from '../payment_enums';
import { getTotalAmount, normalizePaymentType } from '../helpers/helpers';

// * API
import {
  PayPseAPI,
  PayBrebAPI,
  CreateGroupedPaymentAPI,
  getGeneratedQRBrebAPI
} from '../../../API/Payments/PaymentsAPI';
import {
  CreateFinancingTransactionAPI,
  GetPartialPaymentCouponAPI
} from '../../../API/Debts/DebtsAPI';

import {
  DefaultParams,
  buildFinancingRequest,
  buildPSERequest,
  buildBREBRequest
} from './Payment';
import { Company, PortalCheck } from '../../../Configs/general';
import { strSearchParams2Object } from '../../TransactionsList/helpers/helpers';
import {
  InsurancePlan,
  InsuranceProduct,
  InsuranceProductType
} from '../../../Enums/insurances';
import {
  GetInsurancePlanPricesAPI,
  RequestInsuranceAPI,
  VerifyContractEligibilityAPI
} from '../../../API/BrillaInsurances/BrillaInsurancesAPI';
import InsuranceDialog from '../../../Components/Dialogs/CallToActionDialog';
import moment from 'moment';
import { GenerateDuplicateCouponAPI } from '../../../API/Coupons/CouponsAPI';
import { base64ToImage } from '../../../Utils/Transform/Files';
import { InvoicePeriod } from '../../../Enums/invoices';
import { RecaptchaAction } from '../../../Enums/recaptcha';
import TurnstileInvisibleCaptcha from '../../../Components/Captcha/TurnstileInvisibleCaptcha';
import silverPlanImgDesktop from '../../../Assets/images/BANNER_PLAN_SILVERS_386x245_desktop.jpg';
import silverPlanImgMobile from '../../../Assets/images/BANNER_PLAN_SILVERS_328x230_mobile.jpg';
import { Step, StepperProvider } from '../../../Contexts/StepperContext';
import FooterStepper from '../../../Components/Stepper/FooterStepper';
import clsx from 'clsx';
import { useCaptchaWithFallback } from '../../../Hooks/useCaptchaWithFallback';
import BrebQrDialog from '../../../Components/Dialogs/BrebQrDialog';

const PaymentGDC = props => {
  const { match, location = { state: null } } = props;

  const params = location.state ? location.state : DefaultParams;
  const parsedParams = strSearchParams2Object(window.location.search);
  const isMultiplePaymentsParam =
    _get(parsedParams, 'multiplePayments', false) === 'true';

  const backToDeferredPayment = useCallback(() => {
    history.replace(ROUTE_NAMES.deferredPayment, {
      initialStepIndex: 2,
      couponId: params.value,
      addedDeferreds: params.addedDeferreds,
      selectedContract: params.selectedContract
    });
  }, [params.value, params.addedDeferreds, params.selectedContract]);

  if (match && match.params) {
    if (match.params.contractId) {
      if (isMultiplePaymentsParam) {
        params.value = '';
        params.type = TYPE_OF_PAYMENTS.coupon;
      } else {
        params.value = match.params.contractId;
        params.type = TYPE_OF_PAYMENTS.contract;
      }
    } else if (match.params.couponId) {
      params.value = match.params.couponId;
      params.type = TYPE_OF_PAYMENTS.coupon;
    }
  }

  const isSpecialPayment =
    params.fromDeferredPayment || params.type === TYPE_OF_PAYMENTS.financing;
  const requestedPaymentType = params.type;
  params.type = isSpecialPayment
    ? params.type
    : normalizePaymentType(SELECT_OPTIONS, params.type);

  // If the requested type is disabled.
  // clear payload that belongs to the previous type.
  if (!isSpecialPayment && params.type !== requestedPaymentType) {
    params.value = '';
    params.currentStep = 0;
    params.payment = [];
    params.disabledNext = true;
  }

  const paramsPayments = (params || {}).payment;
  const paramsPayment = (paramsPayments || [])[0] || {};
  const defaultPaymentValueOption = paramsPayment.canBePaidPartially
    ? ''
    : PAYMENT_VALUE_OPTION.TotalCoupon;

  // * STATE HOOKS
  const currentUser = useContext(UserContext);
  const setCurrentUser = useContext(UserDispatchContext);
  const setAlert = useContext(AlertsDispatchContext);
  const authToken = _get(currentUser, 'token', null);
  const isIndividualTabParam =
    _get(parsedParams, 'individualTab', false) === 'true';

  const [currentStep, setCurrentStep] = useState(params.currentStep || 0);
  const [goBack, setGoBack] = useState({});
  const [nextButtonText, setNextButtonText] = useState('Siguiente');
  const [number, setNumber] = useState(params.value);
  const [type, setType] = useState(params.type);
  const [loading, setLoading] = useState(false);
  const [disabledNext, setDisabledNext] = useState(
    params.disabledNext !== false
  );
  const [payments, setPayments] = useState(params.payment || []);
  const [methodSelected, setMethodSelected] = useState('');
  const [isCardStep, setCardStep] = useState(false);
  const [payerInfo, setPayerInfo] = useState(null);
  const [insuranceInfo, setInsuranceInfo] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [cardIndividualLoad, setCardIndividualLoad] = useState({
    ...INDIVIDUAL_LOAD_INITIAL_STATE,
    toPay: params.payment || []
  });
  const [cardMultipleLoad, setCardMultipleLoad] = useState(
    MULTIPLE_LOAD_INITIAL_STATE
  );
  const [generatedGroupedCoupon, setGeneratedGroupedCoupon] = useState(null);
  const [paymentsCopy, setPaymentsCopy] = useState([]);
  const [totalCurrentCardsCopy, setTotalCurrentCardsCopy] = useState('');
  const [checkedMultiplePayments, setCheckedMultiplePayments] = useState(
    isMultiplePaymentsParam
  );
  const [currentTabIndex, setCurrentTabIndex] = useState(
    isMultiplePaymentsParam || isIndividualTabParam
      ? INDIVIDUAL_STATE
      : TABS_INITIAL_STATE
  );
  const [showPlanInfo, setShowPlanInfo] = useState(false);
  const [silverPlanPrice, setSilverPlanPrice] = useState(null);
  const [isValidForInsurance, setIsValidForInsurance] = useState(null);
  const [showInsuranceDialog, setShowInsuranceDialog] = useState(false);
  const [paymentValueOption, setPaymentValueOption] = useState(
    defaultPaymentValueOption
  );
  const [updateStepIndex, setUpdateStepIndex] = useState(false);
  const [brebQrImage, setBrebQrImage] = useState('');
  const [brebTransactionId, setBrebTransactionId] = useState('');

  const paymentData = useMemo(() => payments.length && payments[0], [payments]);
  const canPayPartially = paymentData.canBePaidPartially;

  const isFinancingPayment = paymentData.type === TYPE_OF_PAYMENTS.financing;
  const withGroupedCoupon = (paymentData.detail || {}).couponId < 0;

  const isDisableBackCase = isFinancingPayment || withGroupedCoupon;
  const disabledBackButton = currentStep === 1 && isDisableBackCase;

  const isBrebPaymentMethodPending =
    paymentData.detail &&
    paymentData.detail.paymentMethod === PAYMENT_METHOD.BREB &&
    paymentData.detail.isPending &&
    !paymentData.detail.isPaid;

  // * OTHER HOOKS
  const classes = useStyles();
  const {
    captchaRef,
    handlers: captchaHandlers,
    getTokenWithFallback
  } = useCaptchaWithFallback();

  const {
    captchaRef: brebQrCaptchaRef,
    handlers: brebQrCaptchaHandlers,
    getTokenWithFallback: getBrebQrCaptchaToken
  } = useCaptchaWithFallback();

  const isMobileSize = useMediaQuery(theme =>
    theme.breakpoints.down(theme.breakpoints.values.sm)
  );

  useEffect(() => {
    let ignoreRequest = false;
    const fetchData = async () => {
      if (ignoreRequest) {
        return;
      }
      const response = await GetInsurancePlanPricesAPI(authToken);

      if (response.success) {
        const responseData = response.data;

        if (!responseData || !responseData.valid) {
          return;
        }

        const planData = responseData.prices.find(
          ({ id }) => id === InsurancePlan.Silver
        );

        setSilverPlanPrice(planData.price);
      }
    };

    if (!silverPlanPrice && currentUser && !checkedMultiplePayments) {
      fetchData();
    }

    return () => {
      ignoreRequest = true;
    };
  }, [authToken, currentUser, silverPlanPrice, checkedMultiplePayments]);

  useEffect(() => {
    // El usuario puede optar por pagar con otro medio, por eso solo se
    // consulta el QR pendiente cuando explícitamente selecciona Bre-B.
    if (!isBrebPaymentMethodPending || methodSelected !== PAYMENT_METHOD.BREB) {
      return;
    }

    let ignoreRequest = false;

    const fetchPendingBrebQr = async () => {
      const { contractId, couponId } = paymentData.detail;

      // Sin sesión el endpoint exige captcha; con sesión lo resuelve el token.
      let captchaToken = null;
      if (!authToken) {
        captchaToken = await getBrebQrCaptchaToken();
      }

      if (ignoreRequest) {
        return;
      }

      const response = await getGeneratedQRBrebAPI(
        { contractId, couponId, paymentMethod: methodSelected },
        authToken,
        captchaToken
      );

      if (ignoreRequest) {
        return;
      }

      if (!response.success) {
        defaultAlertError(response.error, setAlert);
        return;
      }

      const qr = _get(response.data, 'qr') || _get(response.data, 'data.qr');
      if (qr) {
        setBrebTransactionId(
          _get(response.data, 'transactionId') ||
            _get(response.data, 'data.transactionId')
        );
        setBrebQrImage(base64ToImage(qr));
      } else {
        setAlert({
          type: ALERT_TYPE.WARNING,
          message: 'No se pudo obtener el código QR. Intenta de nuevo.'
        });
      }
    };

    fetchPendingBrebQr();

    return () => {
      ignoreRequest = true;
    };
  }, [
    isBrebPaymentMethodPending,
    methodSelected,
    paymentData,
    authToken,
    getBrebQrCaptchaToken,
    setAlert
  ]);

  useEffect(() => {
    const paymentDetails = payments.length && payments[0].detail;
    const contractId = paymentDetails && paymentDetails.contractId;

    if (checkedMultiplePayments) {
      setIsValidForInsurance(false);
      return;
    }

    let ignoreRequest = false;

    const fetchData = async () => {
      if (ignoreRequest) {
        return;
      }
      const response = await VerifyContractEligibilityAPI(
        currentUser.token,
        contractId,
        InsuranceProductType.Life,
        18100
      );
      if (response.success) {
        setIsValidForInsurance(response.valid);
      }
    };

    if (
      isValidForInsurance === null &&
      silverPlanPrice &&
      currentUser &&
      contractId &&
      !checkedMultiplePayments
    ) {
      fetchData();
    }

    return () => {
      ignoreRequest = true;
    };
  }, [
    currentUser,
    silverPlanPrice,
    payments,
    checkedMultiplePayments,
    isValidForInsurance
  ]);

  // * FUNCTIONS

  const handleGoBack = useCallback(() => {
    setPayerInfo(null);
    setShowPlanInfo(false);
    if (
      payments.length &&
      (payments[0].type === TYPE_OF_PAYMENTS.couponless || params.meterRecharge)
    ) {
      history.goBack();
    }
    setCurrentStep(currentStep - 1);
  }, [currentStep, setCurrentStep, payments, params.meterRecharge]);

  const handleGoToCardStep = useCallback(() => {
    setCardStep(true);
  }, [setCardStep]);

  const totalIndividualCards = useMemo(() => {
    return [
      ...cardIndividualLoad.error,
      ...cardIndividualLoad.pending,
      ...cardIndividualLoad.paid,
      ...cardIndividualLoad.toPay,
      ...cardIndividualLoad.loading
    ];
  }, [cardIndividualLoad]);

  const totalMultipleLoadCards = useMemo(() => {
    return [
      ...cardMultipleLoad.error,
      ...cardMultipleLoad.pending,
      ...cardMultipleLoad.paid,
      ...cardMultipleLoad.toPay,
      ...cardMultipleLoad.loading
    ];
  }, [cardMultipleLoad]);

  const totalCurrentCards = useMemo(() => {
    return [...totalIndividualCards, ...totalMultipleLoadCards];
  }, [totalIndividualCards, totalMultipleLoadCards]);

  const handleSaveCopies = useCallback(() => {
    setPaymentsCopy(JSON.stringify(payments));
    setTotalCurrentCardsCopy(JSON.stringify(totalCurrentCards));
  }, [payments, totalCurrentCards]);

  const handleGoForward = useCallback(() => {
    handleSaveCopies();
    setCurrentStep(currentStep + 1);
    setUpdateStepIndex(true);
  }, [currentStep, setCurrentStep, handleSaveCopies]);

  const handleGroupedCouponPayment = useCallback(async () => {
    if (!(payments.length > 0)) {
      return;
    }

    const totalPayment = getTotalAmount(payments);
    try {
      const { success, data } = await CreateGroupedPaymentAPI(
        payments,
        totalPayment
      );
      if (success) {
        const addressFirstContract = payments[0].detail.contractAddress;
        setPayerInfo(prev => ({
          ...prev,
          addressFirstContract
        }));

        setGeneratedGroupedCoupon(data.data.couponId);
        setGeneratedGroupedCoupon({
          type: 'coupon',
          couponId: data.data.couponId,
          detail: {
            expirationDate: '-',
            generationDate: '-',
            contractAddress: null,
            contractCity: null,
            isPaid: false,
            isPending: false,
            couponId: data.data.couponId,
            contractId: '-',
            value: totalPayment
          }
        });
        handleGoForward();
      }
    } catch (err) {
      setAlert({
        type: ALERT_TYPE.WARNING,
        message: `Ha ocurrido un error, por favor revise ${Company.contractConjugation.regular.plural.article} y cupones ingresados`,
        onClose: () => history.push(`${ROUTE_NAMES.payments}`)
      });
      return;
    }
  }, [handleGoForward, payments, setAlert]);

  const insuranceRequest = useCallback(
    async couponId => {
      if (currentUser && showPlanInfo) {
        const { gender, dateOfBirth, expeditionDate } = insuranceInfo;
        const insureds = [
          {
            firstName: currentUser.firstName,
            lastName: currentUser.lastName,
            identificationType: payerInfo.typeOfIdentification,
            identification: payerInfo.identification.toString(),
            dateOfBirth: moment(dateOfBirth).toISOString(),
            expeditionDate: moment(expeditionDate).toISOString(),
            phone: payerInfo.phone.toString(),
            phoneCountryCode: payerInfo.phoneCountryCode,
            gender,
            email: payerInfo.email
          }
        ];

        const insuranceData = {
          insureds,
          plan: InsurancePlan.Silver,
          product: InsuranceProduct.Practiseguro,
          value: insuranceInfo.price,
          beneficiaries: [],
          contractId: payments[0].detail.contractId,
          couponId
        };

        const response = await RequestInsuranceAPI(
          currentUser.token,
          insuranceData
        );

        if (response.data && response.data.status === 'success') {
          const insuranceRequestId = response.data.data.requestId;
          return {
            insuranceData: { ...insuranceData, insuranceRequestId }
          };
        }

        return null;
      }
    },
    [currentUser, showPlanInfo, payerInfo, payments, insuranceInfo]
  );

  const getPartialCoupon = useCallback(
    async payment => {
      const { partialData, detail } = payment;
      const {
        totalCurrentDebtValue,
        partialPayment,
        productsDetails
      } = partialData;

      const body = {
        totalCurrentDebtValue,
        partialPayment,
        productsDetails,
        referenceCouponId: detail.couponId
      };

      let recaptchaToken;
      if (!authToken) {
        recaptchaToken = await getTokenWithFallback();
      }

      const { success, data, error } = await GetPartialPaymentCouponAPI(
        detail.contractId,
        body,
        authToken,
        recaptchaToken
      );

      if (success) {
        const { couponId } = data.data;
        return couponId;
      }
      defaultAlertError(error, setAlert);
    },
    [authToken, setAlert, getTokenWithFallback]
  );

  const getDuplicateCoupon = useCallback(
    async (contractId, period, origin) => {
      const { success, error, data } = await GenerateDuplicateCouponAPI(
        contractId,
        period,
        authToken,
        origin
      );

      if (success) {
        const { couponId } = data.data;
        if (paymentValueOption === PAYMENT_VALUE_OPTION.CurrentDebt) {
          setPayments([
            {
              ...paymentData,
              partialData: { ...paymentData.partialData, couponId }
            }
          ]);
        }
        return couponId;
      }

      defaultAlertError(error, setAlert);
    },
    [authToken, setAlert, paymentValueOption, paymentData]
  );

  const getCouponIdHandler = useCallback(
    async _evt => {
      const { partialData, detail } = paymentData;
      const { couponId: generatedCouponId } = partialData || {};

      switch (paymentValueOption) {
        case PAYMENT_VALUE_OPTION.TotalCoupon:
          return detail.couponId;
        case PAYMENT_VALUE_OPTION.CurrentDebt: {
          if (paymentData.type === TYPE_OF_PAYMENTS.coupon) {
            return detail.couponId;
          }

          // This happens when the user download the coupon payment
          // coupon first and then try to do the payment
          if (generatedCouponId) {
            return generatedCouponId;
          }

          return await getDuplicateCoupon(
            detail.contractId,
            InvoicePeriod.Current,
            {
              componentId: FORM_ID,
              url: window.location.pathname
            }
          );
        }
        case PAYMENT_VALUE_OPTION.Partial: {
          // This happens when the user download the partial payment
          // coupon first and then try to do the payment
          if (generatedCouponId) {
            return generatedCouponId;
          }

          return await getPartialCoupon(paymentData);
        }

        default:
          return;
      }
    },
    [paymentValueOption, getPartialCoupon, paymentData, getDuplicateCoupon]
  );

  const handleBrebQrClose = useCallback(() => {
    setBrebQrImage('');
    history.replace(
      brebTransactionId
        ? `${ROUTE_NAMES.transaction}/${brebTransactionId}`
        : ROUTE_NAMES.home
    );
  }, [brebTransactionId]);

  const handleGatewayPayment = useCallback(
    async couponId => {
      if (!(payments.length > 0) || !couponId) {
        return;
      }

      // pre-request states
      setLoading(true);
      setDisabledNext(true);

      if (
        isValidForInsurance &&
        !showInsuranceDialog &&
        !showPlanInfo &&
        paymentValueOption !== PAYMENT_VALUE_OPTION.Partial &&
        !paymentData.isPartialCoupon
      ) {
        setShowInsuranceDialog(true);
        setLoading(false);
        setDisabledNext(false);
        return;
      }

      setShowInsuranceDialog(false);

      // request Insurances API

      const insuranceResponse = await insuranceRequest(couponId);

      const insuranceData = insuranceResponse
        ? insuranceResponse.insuranceData
        : null;

      const isBrebPayment = payerInfo.type === PAYMENT_METHOD.BREB;

      // Validar que BREB sea soportado en este portal
      if (isBrebPayment && !PortalCheck.isGascaribe) {
        setAlert({
          type: ALERT_TYPE.ERROR,
          message: 'El método de pago BRE-B no está disponible en tu portal'
        });
        setLoading(false);
        setDisabledNext(false);
        return;
      }

      const buildRequest = isBrebPayment ? buildBREBRequest : buildPSERequest;
      const payAPI = isBrebPayment ? PayBrebAPI : PayPseAPI;

      // request to api
      const body = buildRequest(couponId, payerInfo);

      // ----- Aca cambiar la redireccion si es breb ya que mostraria el QR.

      let transactionType;
      if (paymentValueOption === PAYMENT_VALUE_OPTION.Partial) {
        transactionType = TRANSACTION_TYPES.PartialPayment;
      } else if (params.meterRecharge) {
        transactionType = TRANSACTION_TYPES.MeterRecharge;
      }
      const { success, data, error } = await payAPI(
        {
          ...body,
          product: {
            ...body.product,
            couponId,
            donationValue: 0,
            ...params.productAdditionalData
          },
          insuranceData: insuranceData
            ? { policyData: { ...insuranceData } }
            : null,
          transactionType
        },
        _get(currentUser, 'token', null)
      );

      if (success && isBrebPayment) {
        const qr = _get(data, 'qr') || _get(data, 'data.qr');
        setLoading(false);

        if (qr) {
          setBrebTransactionId(
            _get(data, 'transactionId') || _get(data, 'data.transactionId')
          );
          setBrebQrImage(base64ToImage(qr));
          return;
        }
      }

      // update last bank only applies to PSE payments made by logged users

      if (currentUser && !isBrebPayment) {
        setCurrentUser(user => ({
          ...user,
          lastSelectedBank: payerInfo.currentBank
        }));
      }

      if (success) {
        const transaction = data.data;

        if (
          transaction.status === TRANSACTION_STATUS.Pending &&
          transaction.bankUrl
        ) {
          window.location.href = transaction.bankUrl;
          return;
        }

        if (data.status === 'warning') {
          setAlert({
            type: ALERT_TYPE.WARNING,
            message: data.message,
            onClose: () =>
              history.push(
                `${ROUTE_NAMES.transaction}/${transaction.transactionId}`
              )
          });

          return;
        }

        history.replace(
          `${ROUTE_NAMES.transaction}/${transaction.transactionId}`
        );

        return;
      }
      defaultAlertError(error, setAlert);
      setDisabledNext(false);
      setLoading(false);
    },
    [
      payments,
      setLoading,
      setDisabledNext,
      payerInfo,
      currentUser,
      setAlert,
      setCurrentUser,
      insuranceRequest,
      isValidForInsurance,
      showInsuranceDialog,
      showPlanInfo,
      paymentValueOption,
      paymentData.isPartialCoupon,
      params
    ]
  );

  const handleFinancingPayment = useCallback(async () => {
    if (!(payments.length > 0)) {
      return;
    }

    setLoading(true);
    setDisabledNext(true);

    const paymentDetails = payments[0].detail;

    const financingInfo = {
      price: paymentDetails.totalInitialInstallmentValue,
      contractId: paymentDetails.contractId
    };

    const { requestId } = paymentDetails;
    const selectedPaymentMethod = methodSelected || PAYMENT_METHOD.PSE;
    const isBrebPayment = selectedPaymentMethod === PAYMENT_METHOD.BREB;

    const body = buildFinancingRequest(
      financingInfo,
      payerInfo,
      requestId,
      selectedPaymentMethod
    );

    const transactionResponse = await CreateFinancingTransactionAPI(
      authToken,
      body
    );

    if (transactionResponse.success) {
      const transaction = transactionResponse.data.data;

      if (transaction.status === TRANSACTION_STATUS.Pending && isBrebPayment) {
        const qr = _get(transaction, 'qr');
        if (qr) {
          setLoading(false);
          setBrebTransactionId(transaction.transactionUuid);
          setBrebQrImage(base64ToImage(qr));
          return;
        }
      }

      if (transaction.status === TRANSACTION_STATUS.Pending) {
        window.location.href = transaction.bankUrl;
        return;
      }
      history.replace(
        `${ROUTE_NAMES.transaction}/${transaction.transactionUuid}`
      );

      return;
    }

    const error = extractErrorMessage(transactionResponse);
    setAlert({
      type: ALERT_TYPE.ERROR,
      message: error.message
    });

    setDisabledNext(false);
    setLoading(false);
  }, [authToken, payments, payerInfo, methodSelected, setAlert]);

  const handleSetPayment = useCallback((value, scrollRef) => {
    setPayments(value);
    if (scrollRef) {
      window.scrollTo(0, document.body.scrollHeight);
    }
  }, []);

  const handlePaymentByType = useCallback(
    async evt => {
      switch (type) {
        case TYPE_OF_PAYMENTS.financing:
          return handleFinancingPayment();
        case TYPE_OF_PAYMENTS.contract:
        case TYPE_OF_PAYMENTS.coupon:
          if (payments.length === 1) {
            setLoading(true);
            const couponId = await getCouponIdHandler(evt);
            setLoading(false);
            return handleGatewayPayment(couponId);
          }
          return handleGatewayPayment(generatedGroupedCoupon.couponId);
        default:
          return;
      }
    },
    [
      generatedGroupedCoupon,
      handleFinancingPayment,
      handleGatewayPayment,
      payments,
      type,
      getCouponIdHandler
    ]
  );

  const handleRemoveInvalidCardsAndForward = () => {
    setCardIndividualLoad(prev => ({
      ...prev,
      loading: [],
      paid: [],
      pending: [],
      error: []
    }));

    setCardMultipleLoad(prev => ({
      ...prev,
      loading: [],
      paid: [],
      pending: [],
      error: []
    }));

    setAlert({
      type: ALERT_TYPE.SUCCESS,
      timeout: 5000,
      toast: true,
      message: 'Han sido removidos exitosamente'
    });

    handleTransactionCase();
    setOpenDialog(false);
  };

  const handleTransactionCase = useCallback(() => {
    const currentPaymentsString = JSON.stringify(payments);
    const totalCurrentCardsString = JSON.stringify(totalCurrentCards);

    const allCardsEqual =
      currentPaymentsString === paymentsCopy &&
      totalCurrentCardsString === totalCurrentCardsCopy;

    if (payments.length === 1) {
      handleGoForward();
    } else if (allCardsEqual) {
      handleGoForward();
    } else {
      handleGroupedCouponPayment();
    }
  }, [
    handleGoForward,
    handleGroupedCouponPayment,
    payments,
    paymentsCopy,
    totalCurrentCards,
    totalCurrentCardsCopy
  ]);

  const handleElementStatus = useCallback(() => {
    const totalInvalidIndividual =
      cardIndividualLoad.loading.length +
      cardIndividualLoad.paid.length +
      cardIndividualLoad.pending.length +
      cardIndividualLoad.error.length;
    const totalInvalidMultiple =
      cardMultipleLoad.loading.length +
      cardMultipleLoad.paid.length +
      cardMultipleLoad.pending.length +
      cardMultipleLoad.error.length;

    if (totalInvalidMultiple + totalInvalidIndividual === 0) {
      return handleTransactionCase();
    }

    return setOpenDialog(true);
  }, [
    cardIndividualLoad.loading.length,
    cardIndividualLoad.paid.length,
    cardIndividualLoad.pending.length,
    cardIndividualLoad.error.length,
    cardMultipleLoad.loading.length,
    cardMultipleLoad.paid.length,
    cardMultipleLoad.pending.length,
    cardMultipleLoad.error.length,
    handleTransactionCase
  ]);

  const handleInsuranceActive = () => {
    setShowInsuranceDialog(false);
    window.scrollTo(0, document.body.scrollHeight);
  };

  const renderDialogActions = () => {
    return (
      <>
        <BaseButton onClick={() => handleRemoveInvalidCardsAndForward()}>
          Entendido
        </BaseButton>
      </>
    );
  };
  const renderDialogContent = useCallback(() => {
    return (
      <>
        <Typography className={classes.dialogText}>
          Al avanzar al siguiente paso, la transacción será realizada solo con
          cupones y/o {Company.contractConjugation.regular.plural.main} en
          estado
          <span className={classes.paymentPending}> Por pagar</span>. Los items
          que no cumplan esta condición serán removidos.
        </Typography>
      </>
    );
  }, [classes.dialogText, classes.paymentPending]);

  return (
    <>
      <BrebQrDialog
        open={Boolean(brebQrImage)}
        qrImage={brebQrImage}
        handleClose={handleBrebQrClose}
      />

      <TurnstileInvisibleCaptcha
        captchaRef={captchaRef}
        onResolved={captchaHandlers.onResolved}
        onExpired={captchaHandlers.onExpired}
        onError={captchaHandlers.onError}
        onLoaded={captchaHandlers.onLoaded}
        action={RecaptchaAction.PartialCouponPayments}
      />

      <TurnstileInvisibleCaptcha
        captchaRef={brebQrCaptchaRef}
        onResolved={brebQrCaptchaHandlers.onResolved}
        onExpired={brebQrCaptchaHandlers.onExpired}
        onError={brebQrCaptchaHandlers.onError}
        onLoaded={brebQrCaptchaHandlers.onLoaded}
        action={RecaptchaAction.BrebPendingQr}
      />
      <div className={clsx([classes.root, classes.stepperContainer])}>
        {openDialog && (
          <BaseDialog
            handleClose={() => setOpenDialog(false)}
            open={openDialog}
            title="Estimado cliente"
            actions={renderDialogActions}
            content={renderDialogContent}
            contentStyle={classes.dialogContent}
            contentSize="small"
          />
        )}
        <Container maxWidth="md" className={classes.stepperContainer}>
          <Stepper steps={PAYMENT_STEPS} currentStep={currentStep} />
        </Container>
        <Container maxWidth="md">
          <StepperProvider
            onSubmit={handlePaymentByType}
            initialStepIndex={params.currentStep || 0}
          >
            <Step>
              <PaymentOptions
                setGoBack={setGoBack}
                setCurrentStep={setCurrentStep}
                formId={FORM_ID}
                currentStepIndex={currentStep}
                setDisabledNext={setDisabledNext}
                setNextButtonText={setNextButtonText}
                originRoute={params.fromDeferredPayment}
                backToDeferredPayment={backToDeferredPayment}
                handleElementStatus={handleElementStatus}
                updateStepIndex={updateStepIndex}
                setUpdateStepIndex={setUpdateStepIndex}
                payments={payments}
                handleSetPayment={handleSetPayment}
                number={number}
                setNumber={setNumber}
                type={type}
                setType={setType}
                cardIndividualLoad={cardIndividualLoad}
                setCardIndividualLoad={setCardIndividualLoad}
                cardMultipleLoad={cardMultipleLoad}
                setCardMultipleLoad={setCardMultipleLoad}
                totalCurrentCards={totalCurrentCards}
                totalIndividualCards={totalIndividualCards}
                totalMultipleLoadCards={totalMultipleLoadCards}
                checkedMultiplePayments={checkedMultiplePayments}
                setCheckedMultiplePayments={setCheckedMultiplePayments}
                currentTabIndex={currentTabIndex}
                setCurrentTabIndex={setCurrentTabIndex}
                isMultiplePaymentsParam={isMultiplePaymentsParam}
                canPayPartially={canPayPartially}
                setMethodSelected={setMethodSelected}
                setPaymentValueOption={setPaymentValueOption}
              />
            </Step>
            <Step>
              <>
                <SecondStep
                  formId={FORM_ID}
                  setGoBack={setGoBack}
                  handleGoToCardStep={handleGoToCardStep}
                  handleGoBack={handleGoBack}
                  setCurrentStep={setCurrentStep}
                  setNextButtonText={setNextButtonText}
                  setPayerInfo={setPayerInfo}
                  setInsuranceInfo={setInsuranceInfo}
                  payment={
                    payments.length === 1 ? payments[0] : generatedGroupedCoupon
                  }
                  methodSelected={methodSelected}
                  setMethodSelected={setMethodSelected}
                  setDisabledNext={setDisabledNext}
                  isCardStep={isCardStep}
                  handlePaymentByType={handlePaymentByType}
                  showPlanInfo={showPlanInfo}
                  setShowPlanInfo={setShowPlanInfo}
                  silverPlanPrice={silverPlanPrice}
                  isValidForInsurance={isValidForInsurance}
                  currentUser={currentUser}
                  paymentValueOption={paymentValueOption}
                  setPaymentValueOption={setPaymentValueOption}
                  setPayment={handleSetPayment}
                  setLoading={setLoading}
                  isMultiplePayment={checkedMultiplePayments}
                  paymentsCount={payments.length}
                  isFinancing={
                    payments.length &&
                    payments[0].type === TYPE_OF_PAYMENTS.financing
                  }
                  isMeterRecharge={params.meterRecharge}
                  isBrebPaymentMethodPending={isBrebPaymentMethodPending}
                />
                <InsuranceDialog
                  open={showInsuranceDialog}
                  imageSrc={
                    isMobileSize ? silverPlanImgMobile : silverPlanImgDesktop
                  }
                  title={<>¡Activa tu seguro!</>}
                  message={
                    <>
                      ¿Te gustaría activar nuestro seguro Plan Silver{' '}
                      <span className={classes.breakText}>
                        {' '}
                        y proteger tu vida?
                      </span>
                    </>
                  }
                  handleClose={() => setShowInsuranceDialog(false)}
                  handleConfirmClick={handlePaymentByType}
                  handleDismissClick={handleInsuranceActive}
                  confirmButtonText="Continuar con el pago"
                  dismissButtonBaseText="Activar mi seguro"
                  variant="contained"
                  buttonProps={{
                    className: classes.insuranceButton
                  }}
                  dismissButtonProps={{
                    variant: 'outlined',
                    className: classes.insuranceButton
                  }}
                />
              </>
            </Step>
          </StepperProvider>
        </Container>
        <FooterStepper
          targetForm={FORM_ID}
          disabledNext={disabledNext}
          goBack={goBack}
          nextButtonText={nextButtonText}
          hideBack={disabledBackButton}
        />
      </div>
      {loading && <FullSizeProgress />}
    </>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    flex: 1
  },
  currentStep: {
    marginBottom: theme.custom.footerHeight.stepper
  },
  stepperContainer: {
    // * Mobile
    [theme.breakpoints.up('xs')]: {
      paddingLeft: 0,
      paddingRight: 0,
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(2)
    },
    [theme.breakpoints.up('xm')]: {
      paddingLeft: 0,
      paddingRight: 0,
      marginTop: theme.spacing(5),
      marginBottom: theme.spacing(5)
    },
    // * Web
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(),
      paddingLeft: 0,
      paddingRight: 0
    }
  },
  stepperBar: {
    [theme.breakpoints.down('sm')]: {
      padding: 0,
      marginBottom: 4
    }
  },
  stepperText: {
    [theme.breakpoints.down('sm')]: {
      fontSize: 12,
      padding: 0
    }
  },
  dialogContent: {
    minHeight: 'unset'
  },
  dialogText: {
    fontSize: 14
  },
  paymentPending: {
    fontWeight: 600,
    color: theme.palette.warning.main
  },
  insuranceButton: {
    fontSize: '0.8em',
    marginTop: theme.spacing(2),
    width: '180px'
  },
  breakText: {
    whiteSpace: 'nowrap'
  }
}));

export default PaymentGDC;

// const dummyCreditBody = {
//   creditCard: {
//     ownerId: '12345',
//     ownerTel: '7563126',
//     ownerName: 'John Doe',
//     ownerEmail: 'payer_test@test.com',
//     ownerIdentificationNumber: '5415668464654',
//     paymentMethod: 'VISA',
//     number: '4111111111111111',
//     securityCode: '123',
//     expirationDate: '2022/12'
//   },
//   customer: {
//     id: '9992913912392',
//     fullName: 'APPROVED',
//     email: 'buyer_test@test.com',
//     contactPhone: '7563126',
//     identificationNumber: '5415668464654',
//     address: 'Cra 39 # 66 - 55',
//     city: 'Barranquilla'
//   },
//   product: {
//     couponId: 605051824
//   }
// };
